/**
 * <product-marquee>
 * Horizontal product carousel using native CSS scroll-snap.
 * - Click nav: jump ~4 slides
 * - Hover nav (desktop only): continuous marquee until mouseleave
 * - Boundary: hide nav button when scrolled to start/end
 *
 * The class is declared inside the registration guard so the script is safe to
 * execute more than once per page (the section can appear multiple times, each
 * emitting its own <script> tag) without throwing "already been declared".
 */
if (!window.customElements.get("product-marquee")) {
  class ProductMarquee extends HTMLElement {
    static JUMP_SLIDES = 4;
    static MARQUEE_PX_PER_FRAME = 2;

    connectedCallback() {
      this.track = this.querySelector("[data-cps-track]");
      this.prevBtn = this.querySelector('[data-cps-nav="prev"]');
      this.nextBtn = this.querySelector('[data-cps-nav="next"]');

      if (!this.track) return;

      this.controller = new AbortController();
      this.rafId = null;
      this.scrollDir = 0;
      this.reduceMotionMq = window.matchMedia("(prefers-reduced-motion: reduce)");

      const { signal } = this.controller;

      this.prevBtn?.addEventListener("click", () => this.jump(-1), { signal });
      this.nextBtn?.addEventListener("click", () => this.jump(1), { signal });

      this.prevBtn?.addEventListener("mouseenter", () => this.startMarquee(-1), { signal });
      this.prevBtn?.addEventListener("mouseleave", () => this.stopMarquee(), { signal });
      this.nextBtn?.addEventListener("mouseenter", () => this.startMarquee(1), { signal });
      this.nextBtn?.addEventListener("mouseleave", () => this.stopMarquee(), { signal });

      this.onScroll = () => this.updateNavVisibility();
      this.track.addEventListener("scroll", this.onScroll, { signal, passive: true });

      this.onResize = () => {
        this.updateNavVisibility();
        this.updateNavPosition();
      };
      window.addEventListener("resize", this.onResize, { signal, passive: true });

      const image = this.track.querySelector('.product-item-thumb');
      if (image && typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(image);
        this.resizeObserver.observe(this.track);
      }
      this.updateNavPosition();
      this.updateNavVisibility();
    }

    disconnectedCallback() {
      this.stopMarquee();
      this.controller?.abort();
      this.resizeObserver?.disconnect();
    }

    /** Center arrows on the rendered image, independent of card text and margins. */
    updateNavPosition() {
      const image = this.track?.querySelector('.product-item-thumb');
      if (!image) return;
      const imageRect = image.getBoundingClientRect();
      if (!imageRect.height) return;
      const top = imageRect.top - this.getBoundingClientRect().top + imageRect.height / 2;
      this.style.setProperty('--cps-nav-top', `${top}px`);
    }

    getSlideStep() {
      const slide = this.track.querySelector(".cps-slide");
      if (!slide) return 0;
      const styles = getComputedStyle(this.track);
      const gap = parseFloat(styles.columnGap || styles.gap) || 0;
      return slide.offsetWidth + gap;
    }

    jump(direction) {
      this.stopMarquee();
      const step = this.getSlideStep();
      if (!step) return;
      this.track.scrollBy({
        left: step * ProductMarquee.JUMP_SLIDES * direction,
        behavior: this.reduceMotionMq.matches ? "auto" : "smooth",
      });
    }

    startMarquee(direction) {
      if (this.rafId || this.reduceMotionMq.matches) return;
      this.scrollDir = direction;
      const tick = () => {
        if (!this.scrollDir || !this.track) return;
        const max = this.track.scrollWidth - this.track.clientWidth;
        const next = this.track.scrollLeft + ProductMarquee.MARQUEE_PX_PER_FRAME * this.scrollDir;
        if (next <= 0 || next >= max) {
          this.track.scrollLeft = Math.max(0, Math.min(max, next));
          this.stopMarquee();
          return;
        }
        this.track.scrollLeft = next;
        this.rafId = requestAnimationFrame(tick);
      };
      this.rafId = requestAnimationFrame(tick);
    }

    stopMarquee() {
      this.scrollDir = 0;
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    }

    updateNavVisibility() {
      if (!this.track) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const atStart = this.track.scrollLeft <= 1;
      const atEnd = this.track.scrollLeft >= max - 1;
      this.prevBtn?.classList.toggle("cps-nav-hidden", atStart);
      this.nextBtn?.classList.toggle("cps-nav-hidden", atEnd || max <= 0);
    }
  }

  window.customElements.define("product-marquee", ProductMarquee);
}
